import { apiService } from '.';
import {
    Pqr,
    PqrFilter,
    PqrFilterWeb,
    PqrSmartHistory,
    PqrStatusHistory,
    PqrSurvey,
} from '../shared/types/modules/pqr';
import { SamplesFilter } from '../shared/types/legalProceedings/samples';
import { SmartSupervisionEnum } from '../shared/types/shared';

export const asyncGetPqrs = async (
    filters: PqrFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get('/api/pqr', {
            limit: pageSize || 20,
            page: page || 0,
            ...filters,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqrDataHistory = async (
    filters: PqrFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get('/api/pqr/history', {
            limit: pageSize || 20,
            page: page || 0,
            ...filters,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetSamplesPqr = async (
    filters: SamplesFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get('/api/pqr/samples', {
            limit: pageSize || 20,
            page: page || 0,
            ...filters,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqr = async (idPqr: string): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.get(`/api/pqr/${idPqr}`);

        return data;
    } catch (error) {}
};

export const asyncGetPqrSurvey = async (
    idPqr: string
): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.get(`/api/pqr/survey/${idPqr}`);
        return data;
    } catch (error) {}
};

export const asyncCreatePqr = async (pqr: Pqr): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.post('/api/pqr', pqr);

        return data;
    } catch (error) {}
};

export const asyncCreatePqrWeb = async (pqr: Pqr): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.post('/api/pqr/pqrweb', pqr);

        return data;
    } catch (error) {}
};

export const asyncUpdatePqr = async (
    id: string,
    pqr: Pqr
): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.patch(`/api/pqr/${id}`, pqr);

        return data;
    } catch (error) {}
};

export const asyncGetPqrSmartHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number,
    type?: SmartSupervisionEnum[]
): Promise<{
    items: PqrSmartHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrSmartHistory[]; length: number };
        } = await apiService.get('/api/pqr/smart-history', {
            pqrId,
            limit: pageSize || 20,
            page: page || 0,
            type: type?.length ? type : undefined,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncFindPublicPqr = async (filters: {
    consecutiveFormat: string;
    document: string;
    typeDocument: string;
}): Promise<PqrFilterWeb | undefined> => {
    try {
        // Realiza la solicitud al endpoint correspondiente
        const { data }: { data: PqrFilterWeb } = await apiService.get(
            '/api/pqr/pqrwebinfo',
            {
                consecutiveFormat: filters.consecutiveFormat,
                document: filters.document,
                typeDocument: filters.typeDocument,
            }
        );

        return data;
    } catch (error) {
        console.error('Error en asyncFindPublicPqr:', error);
        return undefined;
    }
};

export const asyncGetPqrStatusHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrStatusHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrStatusHistory[]; length: number };
        } = await apiService.get(`/api/pqr/status-history/${pqrId}`, {
            limit: pageSize || 20,
            page: page || 0,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncRetryM2 = async (
    pqrId: string
): Promise<
    | {
          type: string;
          result: any;
      }[]
    | undefined
> => {
    try {
        const {
            items: data,
        }: {
            items: { type: string; result: any }[];
        } = await apiService.post(`/api/pqr/retry-super-m2/${pqrId}`, {});

        return data || { items: [], length: 0 };
    } catch (error) {}
};

export const asyncRetryAck = async (
    pqrId: string
): Promise<
    | {
          type: string;
          result: any;
      }[]
    | undefined
> => {
    try {
        const {
            items: data,
        }: {
            items: { type: string; result: any };
        } = await apiService.post(`/api/pqr/retry-ack/${pqrId}`, {});

        return data ? [data] : [];
    } catch (error) {}
};

export const asyncCreatePqrSurvey = async (
    pqrSurvey: PqrSurvey
): Promise<PqrSurvey | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: PqrSurvey;
        } = await apiService.post('/api/pqr/survey', pqrSurvey);

        return data;
    } catch (error) {}
};

export const asyncCanDoSurvey = async (
    id: string
): Promise<
    | {
          canDo: boolean;
          halfName: string;
      }
    | undefined
> => {
    try {
        const {
            items,
        }: {
            items: {
                canDo: boolean;
                halfName: string;
            };
        } = await apiService.get(`/api/pqr/can-do-survey/${id}`);

        return items;
    } catch (error) {}
};
